import { useCallback, useRef } from "react";
import Cookies from "js-cookie";
import axios from "../axiosConfig";
import { getDefaultAuthenticatedRoute } from "../utils/authRoles";

export const AUTH_TOKEN_KEY = "hkeAuthToken";
export const TOUR_LEAD_STORAGE_KEY = "hke_tour_lead";
export const LEGACY_TOUR_LEAD_STORAGE_KEY = "hke.tourLead";
export const TOUR_LEAD_TTL_MS = 1000 * 60 * 60 * 24 * 7;
export const TOKEN_CREATION_TIME_KEY = "tokenCreationTime";

const defaultTourErrorToast = {
  title: "Error",
  description: "Tour login failed. Please try again.",
  status: "error",
  duration: 5000,
  isClosable: true,
};

export const useTourSession = ({ applyUserState, clearUserSession, toast } = {}) => {
  const establishInFlightRef = useRef(null);

  const showTourErrorToast = useCallback(() => {
    if (toast) {
      toast(defaultTourErrorToast);
    }
  }, [toast]);

  const clearAuthArtifacts = useCallback(() => {
    Cookies.remove(AUTH_TOKEN_KEY, { path: "/" });
    Cookies.remove("simulatedMode", { path: "/" });
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_CREATION_TIME_KEY);
    delete axios.defaults.headers.common.Authorization;
    localStorage.removeItem("isTourUser");
    sessionStorage.removeItem("organization_survey_id");
    if (clearUserSession) {
      clearUserSession();
    }
  }, [clearUserSession]);

  const readStoredLead = useCallback(() => {
    const storedPrimary = localStorage.getItem(TOUR_LEAD_STORAGE_KEY);
    const storedLegacy = localStorage.getItem(LEGACY_TOUR_LEAD_STORAGE_KEY);
    const stored = storedPrimary || storedLegacy;
    if (!stored) return null;

    try {
      const parsed = JSON.parse(stored);
      const submittedAt = parsed.submittedAt
        ? new Date(parsed.submittedAt).getTime()
        : null;
      if (submittedAt && Date.now() - submittedAt > TOUR_LEAD_TTL_MS) {
        localStorage.removeItem(TOUR_LEAD_STORAGE_KEY);
        localStorage.removeItem(LEGACY_TOUR_LEAD_STORAGE_KEY);
        return null;
      }

      if (storedLegacy && !storedPrimary) {
        localStorage.setItem(TOUR_LEAD_STORAGE_KEY, JSON.stringify(parsed));
        localStorage.removeItem(LEGACY_TOUR_LEAD_STORAGE_KEY);
      }

      if (parsed && typeof parsed === "object") {
        return parsed;
      }
    } catch (error) {
      console.error("Invalid stored tour lead", error);
    }
    return null;
  }, []);

  const persistLead = useCallback((lead) => {
    localStorage.setItem(
      TOUR_LEAD_STORAGE_KEY,
      JSON.stringify({
        name: lead.name || "",
        email: lead.email || "",
        phone: lead.phone || "",
        submittedAt: new Date().toISOString(),
      }),
    );
  }, []);

  const establishTourSession = useCallback(
    async (lead) => {
      if (establishInFlightRef.current) {
        return establishInFlightRef.current;
      }

      const run = async () => {
        clearAuthArtifacts();
        if (process.env.NODE_ENV === "development") {
          console.log("[Tour] auth token present before submit:", !!localStorage.getItem(AUTH_TOKEN_KEY));
        }

        const response = await axios.post("/tour_leads", { tour_lead: lead });
        const backendRedirectPath = response?.data?.redirect_path;
        const token =
          response?.data?.token ||
          response?.data?.jwt ||
          response?.data?.access_token;

        if (!token) {
          if (process.env.NODE_ENV === "development") {
            console.log("[Tour] /tour_leads response missing token");
          }
          showTourErrorToast();
          return { ok: false, redirectPath: backendRedirectPath || "/app/organization" };
        }

        localStorage.setItem(AUTH_TOKEN_KEY, token);
        localStorage.setItem(TOKEN_CREATION_TIME_KEY, Date.now().toString());
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        if (process.env.NODE_ENV === "development") {
          console.log("[Tour] auth token present after submit:", !!localStorage.getItem(AUTH_TOKEN_KEY));
        }

        const meRes = await axios.get("/users/me", {
          params: { ts: Date.now() },
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });
        const me = meRes?.data;
        const redirectPath = getDefaultAuthenticatedRoute(me);
        if (me?.user_type !== "tour") {
          clearAuthArtifacts();
          showTourErrorToast();
          return { ok: false, redirectPath };
        }

        if (process.env.NODE_ENV === "development") {
          console.log("[Tour] /users/me user_type:", me?.user_type);
        }

        if (applyUserState) {
          applyUserState(me);
        }
        persistLead(lead);
        return { ok: true, redirectPath };
      };

      establishInFlightRef.current = run().finally(() => {
        establishInFlightRef.current = null;
      });
      return establishInFlightRef.current;
    },
    [applyUserState, clearAuthArtifacts, persistLead, showTourErrorToast],
  );

  return {
    clearAuthArtifacts,
    readStoredLead,
    persistLead,
    establishTourSession,
  };
};
